import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Minimal viewBox-based zoom + pan for the two India / state SVG maps.
 *
 * Returns:
 *   - viewBox: string to bind to <svg viewBox={viewBox}>
 *   - zoom / setZoom / reset: zoom controls
 *   - pointerHandlers: attach to the container div for drag-to-pan
 *   - suppressClick(): call inside onClick to swallow the click that follows a drag
 */
export default function useMapZoom({ width, height, min = 1, max = 6, step = 1.4 }) {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 }); // top-left of viewBox in world coords
  const dragStateRef = useRef({
    down: false,
    startX: 0,
    startY: 0,
    panX: 0,
    panY: 0,
    moved: false,
  });

  // Clamp pan whenever zoom changes so we don't fly off-screen
  useEffect(() => {
    setPan((p) => {
      const vbW = width / zoom;
      const vbH = height / zoom;
      const maxX = Math.max(0, width - vbW);
      const maxY = Math.max(0, height - vbH);
      return {
        x: Math.min(Math.max(0, p.x), maxX),
        y: Math.min(Math.max(0, p.y), maxY),
      };
    });
  }, [zoom, width, height]);

  const vbW = width / zoom;
  const vbH = height / zoom;
  const viewBox = `${pan.x} ${pan.y} ${vbW} ${vbH}`;

  const zoomAt = useCallback(
    (factor) => {
      setZoom((z) => {
        const newZ = Math.min(max, Math.max(min, z * factor));
        // Keep the current view centre fixed
        setPan((p) => {
          const cxWorld = p.x + width / z / 2;
          const cyWorld = p.y + height / z / 2;
          const newVbW = width / newZ;
          const newVbH = height / newZ;
          return {
            x: Math.min(Math.max(0, cxWorld - newVbW / 2), Math.max(0, width - newVbW)),
            y: Math.min(Math.max(0, cyWorld - newVbH / 2), Math.max(0, height - newVbH)),
          };
        });
        return newZ;
      });
    },
    [width, height, min, max]
  );

  const zoomIn = useCallback(() => zoomAt(step), [zoomAt, step]);
  const zoomOut = useCallback(() => zoomAt(1 / step), [zoomAt, step]);
  const reset = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // Drag-to-pan pointer handlers on the container div
  const onPointerDown = useCallback((e) => {
    if (zoom <= 1) return;
    dragStateRef.current = {
      down: true,
      startX: e.clientX,
      startY: e.clientY,
      panX: pan.x,
      panY: pan.y,
      moved: false,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [zoom, pan.x, pan.y]);

  const onPointerMove = useCallback(
    (e) => {
      const st = dragStateRef.current;
      if (!st.down) return;
      const dx = e.clientX - st.startX;
      const dy = e.clientY - st.startY;
      if (!st.moved && Math.abs(dx) + Math.abs(dy) < 5) return;
      st.moved = true;
      const rect = e.currentTarget.getBoundingClientRect();
      const worldPerPixelX = vbW / rect.width;
      const worldPerPixelY = vbH / rect.height;
      const nextX = st.panX - dx * worldPerPixelX;
      const nextY = st.panY - dy * worldPerPixelY;
      setPan({
        x: Math.min(Math.max(0, nextX), Math.max(0, width - vbW)),
        y: Math.min(Math.max(0, nextY), Math.max(0, height - vbH)),
      });
    },
    [vbW, vbH, width, height]
  );

  const onPointerUp = useCallback((e) => {
    if (dragStateRef.current.down) {
      dragStateRef.current.down = false;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    }
  }, []);

  const suppressClick = useCallback(() => {
    // Returns true if the last pointer sequence was a drag (so clicks should be ignored)
    return dragStateRef.current.moved;
  }, []);

  return {
    zoom,
    setZoom,
    reset,
    zoomIn,
    zoomOut,
    viewBox,
    pointerHandlers: { onPointerDown, onPointerMove, onPointerUp, onPointerLeave: onPointerUp },
    suppressClick,
  };
}
